import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const TAGS = [
  { value: "", label: "All" },
  { value: "academic", label: "Academic" },
  { value: "family", label: "Family" },
  { value: "friendship", label: "Friendship" },
  { value: "loneliness", label: "Loneliness" },
  { value: "grief", label: "Grief" },
  { value: "hope", label: "Hope" },
];

export default function Feed() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get("/stories", { params: t ? { tag: t } : {} });
      setStories(data);
    } catch {
      toast.error("Could not load stories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tag); }, [tag]);

  const onRelate = async (id) => {
    if (!user) { toast.message("Sign in to leave an I Relate."); return; }
    try {
      const { data } = await api.post(`/stories/${id}/relate`);
      setStories((prev) => prev.map((s) => (s.id === id ? { ...s, ...data } : s)));
    } catch {
      toast.error("Could not register relate.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="page-feed">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
            <span className="editorial-rule" />The feed
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight">
            Honest stories,<br /><em className="italic text-mindora-red">written by people just like you.</em>
          </h1>
        </div>
        <Link
          to="/write"
          data-testid="feed-write-link"
          className="btn-primary rounded-full px-6 py-3 text-sm tracking-wide self-start"
        >
          Write your own
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="feed-tag-filters">
        {TAGS.map((t) => (
          <button
            key={t.value || "all"}
            onClick={() => setTag(t.value)}
            data-testid={`feed-filter-${t.value || "all"}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-all ${
              tag === t.value
                ? "bg-mindora-ink text-mindora-bg border-mindora-ink"
                : "bg-mindora-bg text-mindora-ink border-mindora-line hover:border-mindora-red"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-mindora-muted" data-testid="feed-loading">Gathering stories…</div>
      ) : stories.length === 0 ? (
        <div className="rounded-2xl border border-mindora-line p-12 text-center bg-mindora-panel">
          <p className="font-serif text-2xl text-mindora-ink">No stories yet under this tag.</p>
          <p className="text-mindora-muted mt-2">Maybe yours can be the first.</p>
          <Link to="/write" className="btn-primary inline-block mt-6 rounded-full px-6 py-2.5 text-sm">Write one</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="feed-grid">
          {stories.map((s) => (
            <StoryCard key={s.id} s={s} onRelate={onRelate} />
          ))}
        </div>
      )}
    </div>
  );
}

function StoryCard({ s, onRelate }) {
  return (
    <article className="rounded-3xl bg-mindora-panel border border-mindora-line p-7 flex flex-col shadow-clay hover:shadow-clayHover hover:-translate-y-1 transition-all duration-500" data-testid={`story-card-${s.id}`}>
      <div className="flex items-center justify-between mb-4">
        <Badge variant="outline" className="bg-transparent border-mindora-red text-mindora-red rounded-full px-3 py-0.5 text-[10px] uppercase tracking-[0.18em]">
          {s.tag}
        </Badge>
        <span className="text-[11px] text-mindora-muted">{new Date(s.created_at).toLocaleDateString()}</span>
      </div>
      <Link to={`/story/${s.id}`} className="block group" data-testid={`story-title-link-${s.id}`}>
        <h3 className="font-serif text-2xl leading-snug text-mindora-ink group-hover:text-mindora-red transition-colors">
          {s.title}
        </h3>
      </Link>
      <p className="mt-3 text-sm text-mindora-muted leading-relaxed line-clamp-4 flex-1">
        {s.body}
      </p>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs italic text-mindora-muted">— {s.author_name}</span>
        <button
          onClick={(e) => { e.preventDefault(); onRelate(s.id); }}
          data-testid={`story-relate-button-${s.id}`}
          className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-all ${
            s.i_related
              ? "bg-mindora-red text-mindora-bg border-mindora-red"
              : "bg-transparent text-mindora-ink border-mindora-line hover:border-mindora-red hover:text-mindora-red"
          }`}
        >
          <Heart size={14} fill={s.i_related ? "#FDFBF7" : "none"} className={s.i_related ? "animate-soft-pulse" : ""} />
          I Relate · {s.relate_count}
        </button>
      </div>
    </article>
  );
}
